import { useState } from 'react'
import { ColumnDef } from '@tanstack/react-table'
import { Clinic } from '@magnetar/admin-api-interfaces'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { useViewMode } from '@/stores/viewMode'
import { useViewModeActions } from '@/hooks/useViewModeActions'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { clinicGroupService } from '@/services/clinic_group_service'
import { useAuth } from '@/hooks/useAuth'
import { toast } from 'sonner'
import IfAnyPermission from '@/components/IfAnyPermission'
import { AllowedAction } from '@/data/allowedActions'

export const columns: ColumnDef<Clinic>[] = [
  {
    accessorKey: 'clinic_id',
    header: 'ID',
    cell: ({ row }) => {
      const id: number = row.getValue('clinic_id')
      return (
        <Badge variant="outline" className="font-medium">
          {id}
        </Badge>
      )
    },
  },
  {
    accessorKey: 'clinic_name',
    header: '診所名稱',
    cell: ({ row }) => {
      const clinic = row.original
      return (
        <div>
          {clinic.clinic_name}
          {clinic.alias && <span className="text-muted-foreground ml-1">({clinic.alias})</span>}
        </div>
      )
    },
  },
  {
    accessorKey: 'manager_name',
    header: '聯絡人',
  },
  {
    accessorKey: 'phone',
    header: '電話',
  },
  {
    accessorKey: 'is_active',
    header: '狀態',
    cell: ({ row }) => {
      const is_active: boolean = row.getValue('is_active')
      return (
        <Badge variant={is_active ? 'default' : 'secondary'}>{is_active ? '啟用' : '停用'}</Badge>
      )
    },
  },
  {
    id: 'actions',
    cell: function ActionsCell({ row, table }) {
      const clinic = row.original
      const { id: groupId } = useViewMode()
      const { setClinicMode } = useViewModeActions()
      const { token } = useAuth()
      const [isRemoving, setIsRemoving] = useState(false)

      const handleEdit = () => {
        setClinicMode(clinic.clinic_id)
      }

      const handleRemove = async () => {
        if (!groupId || !token) return
        setIsRemoving(true)
        try {
          const result = await clinicGroupService.removeClinic(groupId, clinic.clinic_id, token)
          if (result.success) {
            toast.success('已將診所從集團中移除')
            // Reload data
            const meta = table.options.meta as { reloadData?: () => void }
            meta?.reloadData?.()
          } else {
            toast.error('移除診所失敗')
          }
        } catch {
          toast.error('移除診所失敗')
        } finally {
          setIsRemoving(false)
        }
      }

      return (
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleEdit}>
            編輯
          </Button>
          <IfAnyPermission
            permissions={[
              { module: 'system_clinic', action: AllowedAction.D },
              { module: 'provider_clinic_management', action: AllowedAction.D },
            ]}
          >
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" size="sm" disabled={isRemoving}>
                  移除
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>確定要移除此診所？</AlertDialogTitle>
                  <AlertDialogDescription>
                    此操作會將「{clinic.clinic_name}」從集團中移除，但不會刪除診所資料。
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>取消</AlertDialogCancel>
                  <AlertDialogAction onClick={handleRemove}>確定移除</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </IfAnyPermission>
        </div>
      )
    },
  },
]
